import Head from "next/head";
import { useEffect } from "react";
import { Link } from "@mui/material";
import NextScript from "next/script";
import { TbShirtSport } from "react-icons/tb";
import { HiOutlineTicket } from "react-icons/hi";
import { MdOutlineEventSeat, MdOutlinedFlag } from "react-icons/md";

import { useRouterExtended } from "@/global/hooks/useRouterExtended";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

export const OrlandoMagicLink = () => (
  <Link
    target="_blank"
    href="https://am.ticketmaster.com/magic/"
    sx={{
      fontWeight: 500,
      color: "primary.main",
      textDecoration: "none",
    }}
  >
    Account Manager
  </Link>
);

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose Your Plan(s)",
    description:
      "Select the plan that is best for you. Stack plans for more Magic action.",
  },
  {
    icon: <TbShirtSport />,
    title: "Login to Account Manager",
    description: (
      <>
        Click Continue and connect your Orlando Magic <OrlandoMagicLink /> using
        our secure Ticketmaster login.
      </>
    ),
  },
  {
    icon: <MdOutlineEventSeat />,
    title: "Select Your Seats",
    description: "Select the seats you wish to purchase for each plan.",
  },

  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description: (
      <>
        Tickets for each game in your plan will be available after schedule
        release. Reach out to Orlando Magic support at{" "}
        <Link
          href="tel:4078962442"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (407) 896-2442
        </Link>{" "}
        for any questions.
      </>
    ),
  },
];

export const faqs = [
  {
    title: "How does the Magic 10-Game Plan work?",
    description:
      "Our 10-Game Plans are a great way to lock in the best seats, save big, and catch exciting matchups all season long. Choose from curated set plans - Big Games, Weekend, Eastern or Western - each designed with no overlap so you can stack plans for even more action. Once the official NBA schedule is announced, the exact games included in your Plan will be revealed.",
  },
  {
    title: "How do I select my seats?",
    description:
      "After you select your plan(s), you will be prompted to select your seats from the available inventory. The seats you select will apply to all games within that specific plan.",
  },
  {
    title: "Where are tickets delivered?",
    description:
      "Tickets will be delivered directly within the Orlando Magic App. You will be notified when tickets are available to be managed.",
  },
  {
    title: "How do I renew my tickets for next season?",
    description:
      "Auto renewal makes being an Orlando Magic Ticket Plan Holder easy by automatically renewing your tickets, doing all the work for you. You don’t have to do a thing for your ticket plan renewal, unless of course, you choose to discontinue your tickets, which will be an option. Each year, you’ll be notified of any price or benefit changes and the deadline by which you can elect to opt-out if you choose.",
  },
];

export const AnalyticsHeader = (props: { teamConfig: TeamConfig }) => {
  const router = useRouterExtended();
  const { teamSchema } = props.teamConfig;

  useEffect(() => {
    const handleRouteChange = (path: string) => {
      try {
        const url = `${window.location.origin}/${path}`;
        if (window?.gtag) {
          window?.gtag("event", "page_view", {
            page_location: url,
          });
        }
      } catch (err) {
        console.log(err);
      }
    };
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);

  return (
    <Head>
      <NextScript
        async
        src="https://www.googletagmanager.com/gtag/js?id=G-XTHMZHVGB4"
      />

      <script
        id={`${teamSchema}-analytics-inline`}
        defer
        nonce={process.env.googleTagManagerNonce}
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag() {
              dataLayer.push(arguments);
            }
            gtag('js', new Date()); gtag('config', 'G-XTHMZHVGB4');
        `,
        }}
      />
    </Head>
  );
};
